import { FORM_SEND_LABEL, QUOTE_CTA_LABEL } from '@/lib/cta-labels';

/**
 * Per-variant wiring for the lead forms: endpoint, redirect, and which
 * fields render. Shared by the React form and its plain HTML twin (the one
 * a browser without JavaScript sees), so the two can never drift apart.
 */
export const LEAD_FORM_VARIANTS = {
  contact: {
    endpoint: '/api/forms/contact',
    successPath: '/contact-success',
    eventFields: false,
    quickEventFields: false,
    messageRequired: false,
    submitLabel: 'Send message',
    messageLabel: 'How can we help?',
    messagePlaceholder: 'Your group size, event dates, goals, and anything else we should know…',
  },
  /** Contact-form fields with quote wording — the inline card on program,
   * category and location pages. Sales sees the page in Convert Page. */
  inquiry: {
    endpoint: '/api/forms/contact',
    successPath: '/contact-success',
    eventFields: false,
    quickEventFields: false,
    // Optional on the inline cards: a required essay is the biggest friction
    // point in a hero form. Sales calls back either way.
    messageRequired: false,
    submitLabel: FORM_SEND_LABEL,
    messageLabel: 'Tell us about your event',
    // No dedicated date/group-size fields on the inline card, so prompt for
    // them here — participant count and dates are what sales needs to quote.
    messagePlaceholder: 'Your group size, event dates, goals, and anything else we should know…',
  },
  quote: {
    endpoint: '/api/forms/quote',
    successPath: '/quote-success',
    eventFields: true,
    quickEventFields: false,
    messageRequired: false,
    submitLabel: QUOTE_CTA_LABEL,
    messageLabel: 'Tell us about your event',
    // The full quote form captures date and group size in their own fields,
    // so the message stays open-ended to avoid asking twice.
    messagePlaceholder: 'Your group, goals, and anything else we should know…',
  },
  /** The Get-Quote modal: the compact contact fields plus the three details
   * that make a quote actionable — participants, event date, one program —
   * posted through the quote pipeline. No event city (sales confirms it) and
   * no second program, to keep the modal short. */
  quickquote: {
    endpoint: '/api/forms/quote',
    successPath: '/quote-success',
    eventFields: false,
    quickEventFields: true,
    messageRequired: false,
    submitLabel: QUOTE_CTA_LABEL,
    messageLabel: 'Anything else?',
    messagePlaceholder: 'Goals, format, or anything else we should know…',
  },
  /**
   * A partner's program showcase (/showcase/<key>): the quick-quote fields,
   * posted with the showcase key so the lead is attributed to the partner.
   * The page passes its own successPath so the thank-you stays in the frame.
   */
  showcase: {
    endpoint: '/api/forms/showcase',
    successPath: '/showcase',
    eventFields: false,
    quickEventFields: true,
    messageRequired: false,
    submitLabel: QUOTE_CTA_LABEL,
    messageLabel: 'Anything else?',
    messagePlaceholder: 'Your venue, goals, or anything else we should know…',
  },
} as const;

export type LeadFormVariant = keyof typeof LEAD_FORM_VARIANTS;

/**
 * "First Last" → first/last for the API, which (like Salesforce) wants both.
 * The form's JS validator now requires two words (lead-form-validation.ts), so
 * the single-word branch only guards the plain no-JS twin, where duplicating
 * the one word into both fields keeps the lead rather than 422-ing on the
 * server's required last name. Used by the React form on submit and by the
 * route handler for the plain twin.
 */
export function splitName(full: string): { firstName: string; lastName: string } {
  const words = full.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return { firstName: words[0] ?? '', lastName: words[0] ?? '' };
  return { firstName: words[0] ?? '', lastName: words.slice(1).join(' ') };
}

/** The compact card's single name field; the route splits it like the React form does. */
export const COMPACT_NAME_FIELD = 'name';

/** The eBook gate posts here; it has no variants. */
export const EBOOK_FORM_ENDPOINT = '/api/forms/ebook';
